/**
 * The SME's surface: gate, conversation, sent.
 *
 * Rendered before any auth gate — the person here has no Mauvely account and
 * must never meet a sign-in page. The link in the URL is the whole credential.
 *
 * The design handoff drew this as a question card with a trail of past answers
 * above it. It is a chat instead, at the product owner's direction: the same
 * live-brief rail, the same voice, but the SME is talking to something rather
 * than filling something in. project/github.md records the change.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ds } from '../ds';
import { Wordmark } from '../components/Wordmark';
import { BriefRail } from '../components/BriefRail';
import { MessageBubble } from '../components/chat/MessageBubble';
import { Composer } from '../components/chat/Composer';
import { TypingDots } from '../components/chat/TypingDots';
import { api, streamTurn, ApiError, type Brief, type ChatMessage, type IntakeStats, type ResolvedLink } from '../api';
import { useIsNarrow } from '../responsive';
import { useStickToBottom } from '../scroll';
import { LAYOUT, RADIUS, SHADOW, display, displayLead, mono, monoPlain, panel } from '../theme';

const EMPTY_BRIEF: Brief = {
  sections: { problem: [], audience: [], success: [], existing: [], constraints: [] },
  flags: [],
  progress: 0,
  askingAbout: null,
  suggestions: [],
  done: false
};

type Phase = 'loading' | 'locked' | 'gate' | 'chat' | 'sent' | 'gone';

/** The slug in `/s/<slug>/<token>` is there so a link reads as something; the
 *  token is the whole credential, and it is all this needs. */
export function Intake({ token }: { token: string }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [link, setLink] = useState<ResolvedLink | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  /** The link's password, once it has been accepted. Identify checks it too. */
  const [password, setPassword] = useState<string | undefined>(undefined);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [brief, setBrief] = useState<Brief>(EMPTY_BRIEF);
  const [stats, setStats] = useState<IntakeStats | null>(null);
  /** The brief has gone. Anything typed after this answers a follow-up. */
  const [submitted, setSubmitted] = useState(false);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const narrow = useIsNarrow();
  /** The growing column: messages and composer both. */
  const column = useRef<HTMLDivElement>(null);
  const { atBottom, jump, follow } = useStickToBottom(phase === 'chat', column);

  /** The link is dead — revoked, replaced or expired under an open tab. */
  const closed = useCallback((err: unknown) => {
    if (err instanceof ApiError && err.status === 404) {
      setFailure(err.message);
      setPhase('gone');
    }
    return null;
  }, []);

  // ── resolve the link ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const answer = await api.intake.resolve(token, password);
        if (cancelled) return;
        if ('needsPassword' in answer) {
          setPhase('locked');
          return;
        }
        const resolved = answer;
        setLink(resolved);

        // Already identified on this browser? Then the cookie answers and the
        // gate is skipped — a reload should not ask again.
        try {
          const state = await api.intake.conversation(resolved.projectId);
          if (cancelled) return;
          if (state.conversation) {
            setMessages(state.messages ?? []);
            setBrief(state.brief ?? EMPTY_BRIEF);
            setStats(state.stats ?? null);
            setSubmitted(state.conversation.state === 'submitted');
            setPhase(
              state.conversation.state === 'submitted' &&
                !(state.messages ?? []).some((m) => m.kind === 'followup')
                ? 'sent'
                : 'chat'
            );
            return;
          }
          setPhase('chat');
          void openConversation(resolved.projectId);
        } catch {
          setPhase('gate');
        }
      } catch (err) {
        if (cancelled) return;
        setFailure(err instanceof ApiError ? err.message : 'That link is no longer available.');
        setPhase('gone');
      }
    })();
    return () => {
      cancelled = true;
    };
    // `slug` is in the URL for readability only; the token is the credential.
    // `password` re-runs this once the password form has it right.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, password]);

  // New content follows the bottom, if that is where they were. `follow` is a
  // no-op otherwise, so this never overrides someone reading back.
  useEffect(follow, [follow, messages.length, streaming, phase]);

  // ── the turn ──────────────────────────────────────────────────────────────
  const runTurn = useCallback(
    async (path: string, body: unknown) => {
      setBusy(true);
      setError(null);
      setStreaming('');
      let text = '';

      await streamTurn(path, body, {
        token: (delta) => {
          text += delta;
          setStreaming(text);
        },
        message: ({ id, seq }) => {
          setMessages((prev) => [
            ...prev,
            { id, seq, role: 'assistant', kind: 'chat', content: text, editedAt: null }
          ]);
          setStreaming('');
        },
        state: (next) => setBrief(next),
        error: (message) => setError(message)
      });

      setStreaming('');
      setBusy(false);
    },
    []
  );

  const openConversation = useCallback(
    (projectId: string) => runTurn(`/projects/${projectId}/messages`, { opening: true }),
    [runTurn]
  );

  const send = useCallback(
    async (content: string, skip = false) => {
      if (!link) return;
      const optimistic: ChatMessage = {
        id: `pending-${Date.now()}`,
        seq: (messages[messages.length - 1]?.seq ?? 0) + 1,
        role: 'sme',
        kind: 'chat',
        content: skip ? 'Skipped.' : content,
        editedAt: null
      };
      setMessages((prev) => [...prev, optimistic]);
      setDraft('');
      await runTurn(`/projects/${link.projectId}/messages`, skip ? { skip: true } : { content });
      // The server assigned the real ids; re-read so an edit has one to name.
      const state = await api.intake.conversation(link.projectId).catch(closed);
      if (state?.messages) setMessages(state.messages);
    },
    [link, messages, runTurn, closed]
  );

  const rewind = useCallback(
    async (messageId: string, content: string) => {
      if (!link) return;
      // Drop the discarded turns straight away — waiting for the round trip
      // would leave answers on screen that the assistant has already forgotten.
      setMessages((prev) => {
        const at = prev.findIndex((m) => m.id === messageId);
        if (at === -1) return prev;
        return [...prev.slice(0, at), { ...prev[at]!, content, editedAt: Math.floor(Date.now() / 1000) }];
      });
      await runTurn(`/projects/${link.projectId}/messages/${messageId}/edit`, {
        content,
        mode: 'rewind'
      });
      const state = await api.intake.conversation(link.projectId).catch(closed);
      if (state?.messages) setMessages(state.messages);
    },
    [link, runTurn, closed]
  );

  const editInPlace = useCallback(
    async (messageId: string, content: string) => {
      if (!link) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId ? { ...m, content, editedAt: Math.floor(Date.now() / 1000) } : m
        )
      );
      try {
        const { brief: next } = await api.intake.editInPlace(link.projectId, messageId, content);
        setBrief(next);
      } catch (err) {
        closed(err);
        setError(err instanceof ApiError ? err.message : 'That edit did not save.');
      }
    },
    [link, closed]
  );

  const finish = useCallback(async () => {
    if (!link) return;
    setBusy(true);
    try {
      const { stats: final } = await api.intake.finish(link.projectId);
      setStats(final);
      setSubmitted(true);
      setPhase('sent');
      window.scrollTo(0, 0);
    } catch (err) {
      closed(err);
      setError(err instanceof ApiError ? err.message : 'That did not send. Try again.');
    } finally {
      setBusy(false);
    }
  }, [link, closed]);

  // ── screens ───────────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <Shell>
        <p style={{ ...mono, padding: '80px 0' }}>Opening</p>
      </Shell>
    );
  }

  if (phase === 'locked') {
    return (
      <Shell>
        <Locked token={token} onOpen={setPassword} />
      </Shell>
    );
  }

  if (phase === 'gone' || !link) {
    return (
      <Shell>
        <div style={{ ...panel, padding: '48px 52px', maxWidth: 620, marginTop: 40 }}>
          {/* Not "expired": the server collapses never-existed, revoked,
              expired and archived into one answer on purpose, so naming a
              cause here would be a guess three times out of four. */}
          <h1 style={display(32)}>This link is closed.</h1>
          <p style={{ ...displayLead(20), marginTop: 12 }}>
            {failure ?? 'Ask whoever sent it for a new one.'}
          </p>
        </div>
      </Shell>
    );
  }

  if (phase === 'gate') {
    return (
      <Shell link={link}>
        <Gate
          link={link}
          token={token}
          password={password}
          onReady={() => {
            setPhase('chat');
            void openConversation(link.projectId);
          }}
        />
      </Shell>
    );
  }

  if (phase === 'sent') {
    return (
      <Shell link={link}>
        <Sent link={link} stats={stats} />
      </Shell>
    );
  }

  const lastSme = [...messages].reverse().find((m) => m.role === 'sme' && m.kind === 'chat');
  let questionIndex = 0;

  return (
    <Shell link={link}>
      <main
        style={{
          display: 'grid',
          gridTemplateColumns: narrow ? 'minmax(0,1fr)' : `minmax(0,1fr) ${LAYOUT.rail}px`,
          gap: 20,
          alignItems: 'start',
          paddingBottom: 28
        }}
      >
        <div
          ref={column}
          style={{
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            // The composer sticks to the bottom of this column, so the column
            // has to reach the fold even when the conversation is one question
            // long — otherwise there is nothing for it to stick within.
            minHeight: 'calc(100dvh - 176px)'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 0 auto' }}>
            {messages.map((message) => {
              const index =
                message.role === 'assistant' && message.kind === 'chat' ? ++questionIndex : null;
              return (
                <MessageBubble
                  key={message.id}
                  message={message}
                  index={index}
                  // Only the latest answer carries the affordance: editing an
                  // earlier one is the rewind, and it is reachable from there.
                  editable={!busy && message.role === 'sme' && message.kind === 'chat'}
                  onRewind={(content) => void rewind(message.id, content)}
                  onEditInPlace={(content) => void editInPlace(message.id, content)}
                />
              );
            })}

            {streaming && (
              <MessageBubble
                message={{
                  id: 'streaming',
                  seq: 0,
                  role: 'assistant',
                  kind: 'chat',
                  content: streaming,
                  editedAt: null
                }}
                index={questionIndex + 1}
                editable={false}
                onRewind={() => {}}
                onEditInPlace={() => {}}
              />
            )}

            {busy && !streaming && (
              <div style={{ padding: '10px 0 10px 48px' }}>
                <TypingDots />
              </div>
            )}

            {error && (
              <div
                style={{
                  margin: '10px 0',
                  padding: '12px 16px',
                  borderRadius: RADIUS.card,
                  background: 'var(--surface-inset)',
                  fontSize: 14,
                  color: 'var(--text-body)'
                }}
              >
                {error}
              </div>
            )}
          </div>

          <div style={{ position: 'sticky', bottom: 16, zIndex: 2, marginTop: 14 }}>
            {!atBottom && <JumpToLatest onClick={jump} />}
            <Composer
              value={draft}
              onChange={setDraft}
              onSend={() => void send(draft.trim())}
              onSkip={() => void send('', true)}
              suggestions={brief.suggestions}
              busy={busy}
              // Never after submission. The brief keeps `done: true` for good,
              // so offering it again on a follow-up visit offered a button
              // whose only outcome was "The brief has been sent."
              done={brief.done && !submitted}
              onFinish={() => void finish()}
              placeholder={lastSme ? 'Your answer…' : 'Start with what you are seeing…'}
            />
          </div>
        </div>

        {!narrow && (
          <BriefRail title={link.title} team={link.team} brief={brief} draft={draft} />
        )}
      </main>
    </Shell>
  );
}

// ── jump to latest ──────────────────────────────────────────────────────────

/**
 * Shown only when the reader has scrolled away from the bottom, which is the
 * same moment the conversation stops following itself. Absolutely positioned
 * off the sticky wrapper's top edge, so it needs no measurement of the
 * composer beneath it.
 */
function JumpToLatest({ onClick }: { onClick: () => void }) {
  const { Icon } = ds();
  return (
    <button
      type="button"
      className="mv-acrylic"
      onClick={onClick}
      style={{
        position: 'absolute',
        top: -44,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        border: '1px solid var(--border-strong)',
        borderRadius: RADIUS.pill,
        padding: '8px 15px',
        fontSize: 13,
        color: 'var(--text-body)',
        boxShadow: SHADOW.hairline,
        whiteSpace: 'nowrap'
      }}
    >
      <Icon name="arrow-down" size={14} />
      Jump to latest
    </button>
  );
}

// ── the gate ────────────────────────────────────────────────────────────────

function Gate({
  link,
  token,
  password,
  onReady
}: {
  link: ResolvedLink;
  token: string;
  password: string | undefined;
  onReady: () => void;
}) {
  const { Button, Input } = ds();
  const [name, setName] = useState(link.smeName);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.intake.identify(token, name.trim(), email.trim() || null, password);
      onReady();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
      setBusy(false);
    }
  };

  return (
    <main style={{ maxWidth: 620, paddingBottom: 120 }}>
      <div style={{ padding: '8px 4px 10px' }}>
        <div style={{ ...mono, marginBottom: 14 }}>Scoping intake · {link.smeName}</div>
        <h1 style={display(44)}>Let&rsquo;s scope this properly.</h1>
        <p style={{ ...displayLead(22), marginTop: 12, maxWidth: 560, textWrap: 'pretty' }}>
          Eight or so questions, and they change depending on what you tell me. {link.requester}{' '}
          gets the brief on the right — not a transcript.
        </p>
      </div>

      <div style={{ ...panel, padding: '32px 36px', marginTop: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Input label="Your name" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            hint="For follow-up questions only."
          />
          {error && <p style={{ margin: 0, fontSize: 14, color: 'var(--danger-500)' }}>{error}</p>}
          <div>
            <Button variant="primary" size="md" onClick={() => void start()} disabled={!name.trim() || busy}>
              Start
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}

// ── locked ──────────────────────────────────────────────────────────────────

/** A link with a password. Nothing about the intake is known until it is right. */
function Locked({ token, onOpen }: { token: string; onOpen: (password: string) => void }) {
  const { Button, Input } = ds();
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      // Checked here so a wrong one is said on this form; the page then
      // resolves again with it and carries it into identify.
      const answer = await api.intake.resolve(token, value);
      if ('needsPassword' in answer) throw new Error();
      onOpen(value);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work. Try again.');
      setBusy(false);
    }
  };

  return (
    <main style={{ maxWidth: 620, paddingBottom: 120 }}>
      <div style={{ ...panel, padding: '32px 36px', marginTop: 40 }}>
        <h1 style={display(32)}>This link has a password.</h1>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void open();
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 20 }}
        >
          <Input
            label="Password"
            type="password"
            autoComplete="off"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          {error && <p style={{ margin: 0, fontSize: 14, color: 'var(--danger-500)' }}>{error}</p>}
          <div>
            <Button variant="primary" size="md" type="submit" disabled={!value || busy}>
              Open
            </Button>
          </div>
        </form>
      </div>
    </main>
  );
}

// ── sent ────────────────────────────────────────────────────────────────────

function Sent({ link, stats }: { link: ResolvedLink; stats: IntakeStats | null }) {
  return (
    <main style={{ display: 'flex', justifyContent: 'center', padding: '40px 0 160px' }}>
      <div style={{ ...panel, padding: '48px 52px', maxWidth: 620, width: '100%' }}>
        <div
          style={{
            ...mono,
            fontSize: 9,
            letterSpacing: '.18em',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--signal-hover)',
            marginBottom: 18
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal)' }} />
          Sent
        </div>
        <h1 style={display(40)}>That&rsquo;s everything I need.</h1>
        <p style={{ ...displayLead(22), margin: '14px 0 28px', textWrap: 'pretty' }}>
          Your brief is with {link.requester}. It gets read before the scope meeting, so you
          won&rsquo;t have to say all of that again.
        </p>

        {stats && (
          <div
            style={{
              display: 'flex',
              gap: 28,
              padding: '20px 0',
              borderTop: '1px solid var(--border-subtle)',
              borderBottom: '1px solid var(--border-subtle)'
            }}
          >
            <Figure value={stats.questions} label="Questions" />
            <Figure value={`${stats.minutes} min`} label="Your time" />
            {stats.flags !== undefined && <Figure value={stats.flags} label="Flagged" />}
          </div>
        )}
      </div>
    </main>
  );
}

function Figure({ value, label }: { value: string | number; label: string }) {
  return (
    <div>
      <div style={{ ...display(28), lineHeight: 1 }}>{value}</div>
      <div style={{ ...mono, fontSize: 9, letterSpacing: '.18em', marginTop: 6 }}>{label}</div>
    </div>
  );
}

// ── shell ───────────────────────────────────────────────────────────────────

/**
 * The expert's window.
 *
 * The 2026-09-09 app chrome, and only as much of it as this page should wear:
 * the violet ground and one neutral panel holding the conversation. No rail
 * panel, no strips, no divider under the header — this is a link someone was
 * sent, and a page for a stranger reads as a document rather than as a window
 * of an app they do not have.
 */
function Shell({ children, link }: { children: React.ReactNode; link?: ResolvedLink }) {
  const G = LAYOUT.gutter;
  return (
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        background: 'var(--app-ground)',
        display: 'flex',
        flexDirection: 'column',
        padding: `0 ${G}px ${G}px`
      }}
    >
      {/* `width: 100%` is load-bearing: the shell above is a flex column, and an
          `auto` cross-axis margin cancels the stretch — without it this wrapper
          shrinks to the wordmark and the header floats into the middle. */}
      <div
        style={{ width: '100%', maxWidth: LAYOUT.page, margin: '0 auto', padding: '28px 32px 20px' }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 24,
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
            <Wordmark size={17} />
          </div>
          {link && (
            <div
              style={{
                ...monoPlain,
                display: 'flex',
                alignItems: 'center',
                gap: 18,
                flexWrap: 'wrap'
              }}
            >
              <span>
                Requested by {link.requester}
                {link.team ? ` · ${link.team}` : ''}
              </span>
              <span style={{ width: 1, height: 14, background: 'var(--border-subtle)' }} />
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span
                  style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--signal)' }}
                />
                Saved as you go
              </span>
            </div>
          )}
        </header>
      </div>
      <div
        style={{
          flex: 1,
          background: 'var(--app-panel)',
          borderRadius: RADIUS.appPanel,
          padding: '24px 32px 16px'
        }}
      >
        <div style={{ maxWidth: LAYOUT.page, margin: '0 auto' }}>{children}</div>
      </div>
    </div>
  );
}
